import { useModal } from 'react-modal-hook';
import { useTranslation } from 'react-i18next';
import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import difference from 'lodash/difference';
import uniq from 'lodash/uniq';

import { GetExportsQuery as GetExports, Permission } from '__generated__/graphql';
import { useNotifier } from '../../common/Notifications/NotificationProvider';
import { useSession } from '../Auth/SessionProvider';
import ExportsModal, { ExportsModalProps } from './ExportsModal';
import GetExportsQuery from './GetExportsQuery';
import UI from '../../common/UI';
import useQuery from '../../api/useQuery';

type Export = GetExports['session']['user']['recent_exports'][0];

interface ExportsContextValue {
  pendingExportsCount: number;
  openExportsModal: (initialTab?: ExportsModalProps['initialTab']) => void;
}

export const ExportsContext = React.createContext<ExportsContextValue>({
  pendingExportsCount: 0,
  openExportsModal: () => undefined,
});

interface ExportsProviderProps {
  projectId: string;
  children: ReactNode;
  /** Pass exports to mock the query */
  exports?: Export[];
}

const ExportsProvider = ({ projectId, exports, children }: ExportsProviderProps) => {
  const { t } = useTranslation();
  const notifier = useNotifier();

  const [pollInterval, setPollInterval] = useState(undefined);

  const { hasPermission } = useSession();

  const { data, refetch } = useQuery(GetExportsQuery, {
    variables: {
      project_id: projectId,
    },
    skip: !!exports || !hasPermission(projectId, [Permission.exports]),
    pollInterval,
  });

  const recentExports = data?.session.user.recent_exports || exports || [];
  const exportProfiles = data?.project.export_profiles || [];
  const mailchimpExports = data?.project.mailchimp_exports || [];
  const allExports = [...recentExports, ...mailchimpExports];
  const pendingExports = allExports.filter((recentExport) => !recentExport.finished && !recentExport.failed);

  const [initialTab, setInitialTab] = useState<ExportsModalProps['initialTab']>();

  const [openModal, closeModal] = useModal(() => (
    <ExportsModal
      recentExports={recentExports}
      exportProfiles={exportProfiles}
      mailchimpExports={mailchimpExports}
      initialTab={initialTab}
      close={closeModal}
    />
  ), [recentExports, exportProfiles, mailchimpExports, initialTab]);

  const openExportsModal = useCallback((tab?: ExportsModalProps['initialTab']) => {
    setInitialTab(tab);
    refetch();
    openModal();
  }, [refetch, openModal]);

  useEffect(() => {
    setPollInterval(pendingExports.length > 0 ? 5000 : undefined);
  }, [pendingExports]);

  const pendingExportIdsRef = useRef(pendingExports.map(({ id }) => id));

  useEffect(() => {
    const pendingExportIds = pendingExports.map(({ id }) => id);
    pendingExportIdsRef.current = uniq([...pendingExportIdsRef.current, ...pendingExportIds]);

    if (difference(pendingExportIdsRef.current, pendingExportIds).length > 0) {
      notifier.success(
        <UI.A role="button" onClick={() => openExportsModal()} sc={{ color: 'white' }}>
          {t('notifications.export_done')}
        </UI.A>,
      );

      pendingExportIdsRef.current = pendingExportIds;
    }
  }, [pendingExports, notifier, t, openExportsModal]);

  return (
    <ExportsContext.Provider value={{ pendingExportsCount: pendingExports.length, openExportsModal }}>
      {children}
    </ExportsContext.Provider>
  );
};

export default ExportsProvider;
